/**
 * chatContainer.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/
import bluebird from "bluebird";
import { DateTime } from "luxon";
import * as React from "react";
import { Route, Routes, useLocation, Location, useNavigate, NavigateFunction } from "react-router-dom";

import { GroupNavContext } from "./groupNav.context";

import { AccountScopes, PermissionLevel } from "../models/auth";
import { MOBILE_CUTOFF, ClassToggles } from "../models/constants";
import {
  GroupMembership, Group, GroupType, GroupScopes,
  GroupSubmissionType, GroupMemberType, GroupState
} from "../models/group";
import { RatchetTree } from "../models/crypto";

import { AccountChooser } from "../accounts/accountChooser.component";
import { AccountHeader } from "../headers/accountHeader.component";
import { AccountProfileContainer } from "../profile/acctProfileContainer.component";
import { AccountDisplayFooter } from "../profile/acctDisplayFooter.component";
import { AddGroupDialog } from "../groups/addGroupModal.component";
import { AuthContext } from "../auth/auth.context";
import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { GroupFileListComponent } from "../groups/fileList.component";
import { GroupListContainer } from "./groupList.component";
import { GroupBrowserComponent } from "../groups/groupBrowser.component";
import { GroupChatViewComponent } from "./groupChatView.component";
import { GroupMemberListComponent } from "../groups/memberList.component";
import { GroupSettingsComponent } from "../groups/groupSettings.component";
import { HuddleContainer } from "../streaming/huddleContainer.component";
import { LeadingSideInner } from "../components/leadingSideInner.component";
import { NewDirectMessageComponent } from "../groups/newDirectMessage.component";
import { AccountMemberMention } from "../editor/scEditor";
import { ThreadChatViewWrapper } from "./threadChatView.component";
import { ThreadListViewComponent } from "./threadListView.component";

import { authServiceFactory } from "../services/auth.service";
import { conferenceServiceFactory } from "../services/conferencing.service";
import { dbServiceFactory } from "../services/db.service";
import { groupServiceFactory } from "../services/group.service";
import { workerServiceFactory } from "../services/worker.service";
import { EditMeetingDialog } from "../streaming/editMeetingModal.component";
import { Meeting } from "../models/meeting";
import { adminAccountServiceFactory } from "../services/adminAccount.service";

// import { base64ToBuffer } from "../services/encoding.service";
// import { deserialize, serialize } from "bson";
// import { MessageFrame, MessageFrameType, SenderData } from "../models/message";
// import { cryptoServiceFactory } from "../services/crypto.service";
// import { treeServiceFactory } from "../services/tree.service";


export function ChatContainerWrapper(): React.ReactElement<any, any> {
  const navigate = useNavigate();
  const location = useLocation();
  return <ChatContainer navigate={navigate} location={location} />;
}

interface ChatContainerProps {
  navigate: NavigateFunction
  location: Location
}

interface ChatContainerState extends GroupNavContext {
  loading: boolean,
  loadingGroup: boolean,
  huddleGroup: GroupMembership | null,
  membershipID: string,
  showAddGroupModal: boolean,
  showAddMemberModal: boolean,
  unreadThreadMessages: number,
  unreadThreadMentions: number,
  classes: ClassToggles,
  currentThreadID: string,
  errMsg: string,
  openEditMeetingDialog: boolean,
  lastCreatedMeeting: Meeting | null,
}

export class ChatContainer extends React.Component<ChatContainerProps, ChatContainerState> {
  static contextType = AuthContext;
  declare context: React.ContextType<typeof AuthContext>;

  private mainScrollRef: React.RefObject<HTMLDivElement | null>;
  private searchResolver: ((members: Array<AccountMemberMention>) => void) | null;
  private searchTimer: NodeJS.Timeout | null;
  private refreshPromise: Promise<void> | null;

  constructor(props: ChatContainerProps) {
    super(props);
    this.setCurrentGroup = this.setCurrentGroup.bind(this);
    this.changeGroup = this.changeGroup.bind(this);
    this.setThread = this.setThread.bind(this);
    this.finishAddGroup = this.finishAddGroup.bind(this);
    this.toggleGroupList = this.toggleGroupList.bind(this);
    this.toggleRightDetail = this.toggleRightDetail.bind(this);
    this.hideLeftNav = this.hideLeftNav.bind(this);
    this.visibilityChange = this.visibilityChange.bind(this);
    this.resize = this.resize.bind(this);
    // this.onHoverGroup = this.onHoverGroup.bind(this);
    // this.offHoverGroup = this.offHoverGroup.bind(this);
    this.onNewGroup = this.onNewGroup.bind(this);
    this.didDeleteGroup = this.didDeleteGroup.bind(this);
    this.onGroupUpdate = this.onGroupUpdate.bind(this);
    this.onHuddleStart = this.onHuddleStart.bind(this);
    this.onHuddleEnd = this.onHuddleEnd.bind(this);
    this.onThreadChange = this.onThreadChange.bind(this);
    this.addThisDeviceToGroup = this.addThisDeviceToGroup.bind(this);
    this.recoverGroup = this.recoverGroup.bind(this);
    this.getMatchingMembers = this.getMatchingMembers.bind(this);
    this.navTo = this.navTo.bind(this);
    this.doHuddle = this.doHuddle.bind(this);
    this.didJoinHuddle = this.didJoinHuddle.bind(this);
    this.leaveHuddle = this.leaveHuddle.bind(this);
    this.enableArchiving = this.enableArchiving.bind(this);
    this.can = this.can.bind(this);

    this.mainScrollRef = React.createRef();
    this.searchResolver = null;
    this.searchTimer = null;
    this.refreshPromise = null;
    this.finishEditMeetingDialog = this.finishEditMeetingDialog.bind(this);
    this.closeEditMeetingDialog = this.closeEditMeetingDialog.bind(this);

    this.state = {
      loading: false,
      loadingGroup: false,
      huddleGroup: null,
      membershipID: "",
      currentGroup: null,
      groupMemberships: [],
      directMessages: [],
      groupMembershipMap: {},
      showAddGroupModal: false,
      showAddMemberModal: false,
      unreadThreadMessages: 0,
      unreadThreadMentions: 0,
      currentThreadID: "",
      classes: {
        chatContainer: !/group\/(browse|threads)\/?$/.test(this.props.location.pathname) &&
          !/group\/profile$/.test(this.props.location.pathname) &&
          /group\/\w+\/?((thread|huddle)\/?(\w+)?)?$/.test(this.props.location.pathname),
        showLeftNav: window.innerWidth > MOBILE_CUTOFF,
        showRightDetail: /group\/\w+\/thread\/\w+/.test(props.location.pathname),
        multipleAccounts: false,
        withHuddle: false, // /group\/\w+\/huddle/.test(props.location.pathname),
        hideChatInput: false
      },
      errMsg: "",
      setCurrentGroup: this.setCurrentGroup,
      finishAddGroup: this.finishAddGroup,
      toggleGroupList: this.toggleGroupList,
      toggleRightDetail: this.toggleRightDetail,
      addThisDeviceToGroup: this.addThisDeviceToGroup,
      recoverGroup: this.recoverGroup,
      setThread: this.setThread,
      getMatchingMembers: this.getMatchingMembers,
      mainScrollingElement: (): HTMLElement | null => this.mainScrollRef?.current,
      navTo: this.navTo,
      doHuddle: this.doHuddle,
      didJoinHuddle: this.didJoinHuddle,
      leaveHuddle: this.leaveHuddle,
      enableArchiving: this.enableArchiving,
      can: this.can,
      openEditMeetingDialog: false,
      lastCreatedMeeting: null,
    };
  }

  componentDidMount(): void {
    document.addEventListener("visibilitychange", this.visibilityChange);
    const context: AuthContext = this.context;
    const workerService = workerServiceFactory();
    workerService.on("newgroup", this.onNewGroup);
    workerService.on("delgroup", this.didDeleteGroup);
    workerService.on("group", this.onGroupUpdate);
    workerService.on("thread", this.onThreadChange);
    workerService.on("huddleStart", this.onHuddleStart);
    workerService.on("huddleEnd", this.onHuddleEnd);

    const match = /thread\/([^/]+)\/?$/.exec(this.props.location.pathname);
    if (match) {
      this.setState({ currentThreadID: match[1] });
    }
    if (context.membership && context.membership._id) {
      this.fetchAccountState();
      this.setState({
        membershipID: context.membership._id,
        classes: {
          ...this.state.classes,
          multipleAccounts: context.memberships.length > 1
        }
      });
    }
    this.resize();
    window.addEventListener("resize", this.resize);

  }

  componentDidUpdate(prevProps: ChatContainerProps): void {
    const context: AuthContext = this.context;
    if (context.membership && context.membership._id !== this.state.membershipID) {
      this.fetchAccountState();
      this.setState({
        membershipID: context.membership._id
      });
    }
    if (context.memberships.length > 1 && !this.state.classes.multipleAccounts ||
      context.memberships.length <= 1 && this.state.classes.multipleAccounts) {
      this.setState({
        classes: {
          ...this.state.classes,
          multipleAccounts: context.memberships.length > 1
        }
      });
    }
    if (this.props.location.pathname !== prevProps.location.pathname) {
      const haveHuddle = /group\/\w+\/huddle\/?(\w+)?$/.test(this.props.location.pathname);
      const toggles: Partial<ClassToggles> = {
        chatContainer: !/group\/(browse|threads)\/?$/.test(this.props.location.pathname) &&
          !/group\/profile$/.test(this.props.location.pathname) &&
          /group\/\w+\/?((thread|huddle)\/?(\w+)?)?$/.test(this.props.location.pathname),
      };
      if (haveHuddle && !this.state.classes.withHuddle) {
        toggles.withHuddle = true;
        toggles.showLeftNav = false;
        toggles.showRightDetail = false;
        this.setState({ huddleGroup: this.state.currentGroup });
      }
      this.setState({
        classes: {
          ...this.state.classes,
          ...toggles
        }
      });
    }
  }

  private resize() {
    if (window.innerWidth < MOBILE_CUTOFF || this.state.huddleGroup) {
      this.setState({ classes: { ...this.state.classes, showLeftNav: false } });
    } else {
      this.setState({ classes: { ...this.state.classes, showLeftNav: true } });
    }
  }

  componentWillUnmount(): void {
    document.removeEventListener("visibilitychange", this.visibilityChange);
    window.removeEventListener("resize", this.resize);
    const workerService = workerServiceFactory();
    workerService.off("newgroup", this.onNewGroup);
    workerService.off("delgroup", this.didDeleteGroup);
    workerService.off("group", this.onGroupUpdate);
    workerService.off("thread", this.onThreadChange);
    workerService.off("huddleStart", this.onHuddleStart);
    workerService.off("huddleEnd", this.onHuddleEnd);
  }

  private visibilityChange() {
    if (document.visibilityState !== "visible") { return; }
    if (this.refreshPromise) { return; }

    this.refreshPromise = (async () => {
      const context: AuthContext = this.context;
      if (!context.membership) { return; }
      const promises: Array<Promise<{ member: GroupMembership, isDM: boolean }>> = [];
      for (const member of this.state.groupMemberships) {
        if (member.ongoingHuddle) {
          promises.push(this.checkHuddle(context.membership.accountID, member).then(() => {
            return { member: member, isDM: false };
          }));
        }
      }
      for (const member of this.state.directMessages) {
        if (member.ongoingHuddle) {
          promises.push(this.checkHuddle(context.membership.accountID, member).then(() => {
            return { member: member, isDM: true };
          }));
        }
      }
      const changes = await bluebird.all(promises);
      let newGroupArr: Array<GroupMembership> | null = null;
      let newDMArr: Array<GroupMembership> | null = null;
      for (const member of changes) {
        if (!member.member.ongoingHuddle) {
          if (member.isDM) {
            if (!newDMArr) { newDMArr = Array.from(this.state.directMessages); }
            const index = newDMArr.findIndex(g => g.groupID === member.member.groupID);
            if (index >= 0) {
              newDMArr[index] = member.member;
            }
          } else {
            if (!newGroupArr) { newGroupArr = Array.from(this.state.groupMemberships); }
            const index = newGroupArr.findIndex(g => g.groupID === member.member.groupID);
            if (index >= 0) {
              newGroupArr[index] = member.member;
            }
          }
        }
      }
      if (!newGroupArr && !newDMArr) { return; }
      const update: any = {};
      if (newGroupArr) { update.groupMemberships = newGroupArr; }
      if (newDMArr) { update.directMessages = newDMArr; }
      this.setState(update);
    })();

    this.refreshPromise.finally(() => { this.refreshPromise = null; });
  }

  private hideLeftNav() {
    this.setState({ classes: { ...this.state.classes, showLeftNav: false } });
  }

  private async onNewGroup(/*group: Group*/) {
    this.fetchAccountState();
  }

  private async onThreadChange() {
    const context: AuthContext = this.context;
    if (!context.membership) { return; }
    const threadCounts = await groupServiceFactory().countUnreadParticipatingThreadsForAccount(
      context.membership.accountID);
    this.setState({
      unreadThreadMessages: threadCounts[0],
      unreadThreadMentions: threadCounts[1]
    });
  }

  private async onGroupUpdate(groupID: string) {
    const context: AuthContext = this.context;
    const group = await dbServiceFactory().getGroup(groupID);
    if (!group) { return; }
    if (group.grouptype === GroupType.meeting) { return; }

    const groupMembership: GroupMembership | undefined = this.state.groupMembershipMap[groupID];
    if (!groupMembership) { return; }

    groupMembership.haveRatchetTree = true;
    groupMembership.outOfSync = group.outOfSync;
    groupMembership.unreadMessages = group.unreadMessages;
    groupMembership.unreadMentions = group.unreadMentions;
    groupMembership.lastMsgTime = group.lastMsgTime;
    if (group.welcomed !== undefined) {
      groupMembership.fullyWelcomed = group.welcomed;
    }

    if (context.membership?.mustArchive) {
      groupMembership.needArchiving = !group.archiving;
    }

    console.log("onGroupUpdate, group last message time:", group.lastMsgTime,
      "isDM:", groupMembership.grouptype === GroupType.dm);
    if (groupMembership.groupID === this.state.currentGroup?.groupID) {
      this.setState({ currentGroup: groupMembership });
    }
    this.updateGroupList(groupMembership, true);
  }

  // ratchet tree deleted
  private async didDeleteGroup(groupID: string) {
    const groupMembership: GroupMembership | undefined = this.state.groupMembershipMap[groupID];
    if (!groupMembership) { return; }

    groupMembership.haveRatchetTree = false;

    this.updateGroupList(groupMembership, false, true);
  }

  private async onHuddleStart(groupID: string) {
    console.log("huddle start:", groupID);
    const groupMembership: GroupMembership | undefined = this.state.groupMembershipMap[groupID];

    if (!groupMembership) { return; }
    groupMembership.ongoingHuddle = true;

    this.updateGroupList(groupMembership);
  }

  private async onHuddleEnd(groupID: string) {
    console.log("huddle end:", groupID);
    const groupMembership: GroupMembership | undefined = this.state.groupMembershipMap[groupID];

    if (!groupMembership) { return; }
    groupMembership.ongoingHuddle = false;

    this.updateGroupList(groupMembership);
  }

  private async fetchAccountState(): Promise<void> {
    if (this.state.loading) { return; }
    try {
      const groupService = groupServiceFactory();

      const context: AuthContext = this.context;
      if (!context.membership) {
        console.error("tried fetching groups with no account membership set");
        this.setState({ errMsg: "Missing Account Membership!" });
        return;
      }
      this.setState({ loading: true });
      const { memberships, groups, threadCounts } = await bluebird.props({
        memberships: groupService.getMyGroupMemberships(context.membership.accountID, "chat"),
        groups: groupService.getMyGroups(context.membership.accountID, "chat"),
        threadCounts: groupService.countUnreadParticipatingThreadsForAccount(context.membership.accountID)
      });
      const directMessages: Array<GroupMembership> = [];
      const gMemberships: Array<GroupMembership> = [];
      const groupMemberMap: { [id: string]: GroupMembership } = {};
      const groupMap: { [id: string]: Group } = {};  // don't keep this cached, groups change
      const promises: Array<Promise<void>> = [];

      for (const g of groups) {
        groupMap[g._id] = g;
      }
      for (const member of memberships) {
        const groupstate = member.groupstate || GroupState.active;
        member.submissiontype = member.submissiontype || GroupSubmissionType.normal;
        member.groupstate = groupstate;
        member.archiving = typeof member.archiving === "boolean" ? member.archiving : false;
        groupMemberMap[member.groupID] = member;
        const g = groupMap[member.groupID];
        const devMembership = member.devices.find(
          dMember => dMember._id === groupService.deviceID);
        let needUpdate = false;
        const update: Partial<Group> = {};
        if (context.membership.mustArchive && !member.archiving) {
          member.needArchiving = true;
        }
        if (g) {
          if (g.welcomed !== undefined) {
            member.fullyWelcomed = g.welcomed;
          } else {
            member.fullyWelcomed = true;
          }
          if (g.lastMsgTime) {
            member.lastMsgTime = g.lastMsgTime;
          }
          if (member.groupname !== g.groupname) {
            g.groupname = member.groupname;
            update.groupname = member.groupname;
            needUpdate = true;
          }
          if (g.archiving !== member.archiving) {
            g.archiving = member.archiving;
            update.archiving = member.archiving;
            needUpdate = true;
          }
          if (member.grouptype !== g.grouptype) {
            g.grouptype = member.grouptype;
            update.grouptype = member.grouptype;
            needUpdate = true;
          }
          if (member.submissiontype !== g.submissiontype) {
            g.submissiontype = member.submissiontype;
            update.submissiontype = member.submissiontype;
            needUpdate = true;
          }
          if (needUpdate) {
            await groupService.updateLocalGroup(member.groupID, update);
          }
          if (devMembership) {
            member.haveRatchetTree = true;
            member.outOfSync = g.outOfSync;
            member.unreadMessages = g.unreadMessages;
            member.unreadMentions = g.unreadMentions;
          } else {
            // this device is no longer participating in this group
            // clear it out
            //await groupService.clearGroup(g._id);
          }
        } else {
          member.haveRatchetTree = false;
          member.outOfSync = false;
          member.fullyWelcomed = true;
        }
        if (groupstate === GroupState.active || groupstate === GroupState.frozen) {
          if (member.grouptype === GroupType.dm) {
            if (!member.groupname) {
              promises.push(this.setDirectMessageName(member));
            }
            if (member.haveRatchetTree) {
              directMessages.push(member);
            }
          } else {
            gMemberships.push(member);
          }
        }

        if (member.ongoingHuddle) {
          promises.push(this.checkHuddle(context.membership.accountID, member));
        }
      }
      if (promises.length > 0) {
        await bluebird.all(promises);
      }
      gMemberships.sort(function (a, b): number {
        return a.groupname.localeCompare(b.groupname);
      });
      directMessages.sort(function (a, b): number {
        const aMillis = (a.lastMsgTime || DateTime.fromMillis(0)).toMillis();
        const bMillis = (b.lastMsgTime || DateTime.fromMillis(0)).toMillis();
        return bMillis - aMillis;
      });

      this.setState({
        groupMemberships: gMemberships,
        directMessages: directMessages,
        groupMembershipMap: groupMemberMap,
        unreadThreadMessages: threadCounts[0],
        unreadThreadMentions: threadCounts[1]
      });

    } catch (err) {
      console.error("Error getting groups:", err);
      this.setState({ errMsg: "Error fetching groups: " + (err as Error).message });
    } finally {
      this.setState({ loading: false });
    }
  }

  private async checkHuddle(accountID: string, groupMembership: GroupMembership): Promise<void> {
    const room = await conferenceServiceFactory().getRoom(accountID, groupMembership.groupID);
    if (!room) {
      groupMembership.ongoingHuddle = false;
    }
  }

  public doHuddle(groupID: string): void {
    console.log("huddle:", groupID);
    this.props.navigate(`/group/${groupID}/huddle`);
    const groupMembership: GroupMembership | undefined = this.state.groupMembershipMap[groupID];

    if (!groupMembership) { return; }

    this.setState({
      huddleGroup: groupMembership,
      classes: {
        ...this.state.classes,
        withHuddle: true,
        showLeftNav: false,
        showRightDetail: false
      }
    });
  }

  public didJoinHuddle(groupID: string): void {
    const groupMembership: GroupMembership | undefined = this.state.groupMembershipMap[groupID];

    if (!groupMembership) { return; }
    groupMembership.ongoingHuddle = true;

    this.updateGroupList(groupMembership);
  }

  public async leaveHuddle(): Promise<void> {
    if (!this.state.huddleGroup) { return; }
    try {
      if (this.state.huddleGroup.ongoingHuddle) {
        const stillInSession = await conferenceServiceFactory().leaveRoom(
          this.state.huddleGroup.accountID, this.state.huddleGroup.groupID);
        if (!stillInSession) {
          this.state.huddleGroup.ongoingHuddle = false;
        }
      }
    } catch (err) {
      console.error("Error leaving huddle:", err);
    } finally {
      this.setState({
        huddleGroup: null,
        classes: {
          ...this.state.classes,
          withHuddle: false,
          showLeftNav: true
        }
      });
      if (this.state.currentGroup) {
        this.props.navigate(`/group/${this.state.currentGroup.groupID}`);
      }
    }
  }

  public navTo(url: string, leaveRight = false): void {
    if (leaveRight || !this.state.classes.showRightDetail) {
      this.props.navigate(url);
      if (window.innerWidth < MOBILE_CUTOFF) {
        this.setState({ classes: { ...this.state.classes, showLeftNav: false } });
      }
      return;
    }

    this.setState({
      currentThreadID: "",
      classes: {
        ...this.state.classes,
        showRightDetail: false
      }
    });
    if (window.innerWidth < MOBILE_CUTOFF) {
      this.setState({ classes: { ...this.state.classes, showLeftNav: false } });
    }
    setTimeout(() => {
      this.props.navigate(url);
    }, 500);
  }

  private async changeGroup(group: GroupMembership) {
    await this.setCurrentGroup(group);

    let url = `/group/${group.groupID}`;
    if (this.state.currentThreadID) {
      url += `/thread/${this.state.currentThreadID}`;
    }
    if (this.props.location.pathname !== url) {
      this.props.navigate(url);
    }
  }

  public async setCurrentGroup(group: GroupMembership | null): Promise<RatchetTree | null> {
    const rtree = await groupServiceFactory().setCurrentGroupMembership(group);

    const hideChatInput = group && group.haveRatchetTree &&
      group.submissiontype === GroupSubmissionType.broadcastOnly &&
      group.groupMemberType === GroupMemberType.observer;

    const update: any = {
      currentGroup: group,
      classes: {
        ...this.state.classes,
        hideChatInput: hideChatInput || false
      }
    };
    // close the drawer if on mobile
    if (window.innerWidth < MOBILE_CUTOFF) {
      update.classes.showLeftNav = false;
    }
    if (group && group.grouptype === GroupType.dm &&
      !this.state.directMessages.find(g => g.groupID === group.groupID)) {
      this.state.directMessages.unshift(group);
      update.directMessages = Array.from(this.state.directMessages);
    }
    this.setState(update);
    return rtree;
  }

  public async setThread(threadID: string): Promise<void> {
    if (!this.state.classes.showRightDetail) {
      this.setState({
        currentThreadID: threadID,
        classes: {
          ...this.state.classes,
          showRightDetail: true
        }
      });
    } else {
      this.setState({ currentThreadID: threadID });
    }
    if (this.state.currentGroup) {
      this.props.navigate(
        `/group/${this.state.currentGroup.groupID}/thread/${threadID}`);
    }
  }

  private can(level: PermissionLevel, perm: keyof GroupScopes,
    groupID?: string,
    acctPerm: keyof AccountScopes = "groups"): boolean {
    if (this.context.can(PermissionLevel.ADMIN, acctPerm)) { return true; }

    const group = groupID ? this.state.groupMembershipMap[groupID] : this.state.currentGroup;
    if (!group) { return false; }
    const curPerm = group.perms[perm] || PermissionLevel.NONE;
    return (curPerm & level) === level;
  }

  private async setDirectMessageName(member: GroupMembership) {
    const groupService = groupServiceFactory();
    const context: AuthContext = this.context;
    if (!context.membership) {
      return;
    }
    const memberships = await groupService.getGroupMembers(context.membership.accountID, member.groupID);
    const userIDs = new Set(memberships.map(m => m.userID));
    const acctMemberMap = await authServiceFactory().membersFor(userIDs);
    member.groupname = groupService.makeDirectMessageName(
      member, memberships, acctMemberMap);
    await groupService.updateMyMembership(context.membership.accountID, {
      groupID: member.groupID,
      userID: member.userID,
      groupMemberType: member.groupMemberType,
      groupname: member.groupname
    });
  }

  public async finishAddGroup(group: Group): Promise<[RatchetTree | null, GroupMembership | null]> {
    const groupService = groupServiceFactory();
    let membership: GroupMembership | null = null;
    let rtree: RatchetTree | null = null;

    if (!this.context.membership) {
      throw new Error("Missing Account Information");
    }

    group.accountID = this.context.membership.accountID;

    [group, membership] = await groupService.createGroup(
      this.context.membership.accountID, group,
      this.context.membership.userID);

    const update: any = { showAddGroupModal: false };

    if (membership) {
      membership.haveRatchetTree = true;
      update.groupMembershipMap = { ...this.state.groupMembershipMap };
      update.groupMembershipMap[membership.groupID] = membership;
      if (membership.grouptype === GroupType.dm) {
        // Don't bother updating groupname here, not all members have been added yet
        this.state.directMessages.unshift(membership);
        update.directMessages = Array.from(this.state.directMessages);
      } else {
        update.groupMemberships = this.state.groupMemberships.concat(membership);
      }
      update.currentGroup = membership;
      rtree = await groupService.setCurrentGroupMembership(membership);
      this.state.groupMembershipMap[membership.groupID] = membership;
    }
    this.setState(update);
    return [rtree, membership];
  }

  public async addThisDeviceToGroup(groupID?: string): Promise<any> {
    const context: AuthContext = this.context;
    if (!groupID) {
      this.setState({ errMsg: "Missing group ID" });
      return;
    }
    if (!context.membership) {
      this.setState({ errMsg: "Missing Account Info!" });
      return;
    }

    try {
      const groupService = groupServiceFactory();
      const existing: GroupMembership | undefined = this.state.groupMembershipMap[groupID];

      const search = (g: GroupMembership) => g.groupID === groupID;

      const [, newMembership] = await groupService.join(context.membership.accountID,
        context.membership.userID, groupID, existing) as [Group, GroupMembership];

      this.state.groupMembershipMap[newMembership.groupID] = newMembership;
      if (newMembership.grouptype === GroupType.dm) {

        const newArr = Array.from(this.state.directMessages);
        newMembership.haveRatchetTree = true;
        const index = newArr.findIndex(search);
        if (index < 0) {
          newArr.push(newMembership);
        } else {
          newArr[index] = newMembership;
        }
        newArr.sort(function (a, b): number {
          const aMillis = (a.lastMsgTime || DateTime.fromMillis(0)).toMillis();
          const bMillis = (b.lastMsgTime || DateTime.fromMillis(0)).toMillis();
          return bMillis - aMillis;
        });
        this.setState({ directMessages: newArr });
        return;

      } else if (newMembership.grouptype !== GroupType.meeting) {
        newMembership.haveRatchetTree = true;
        const newArr = Array.from(this.state.groupMemberships);
        const index = newArr.findIndex(search);
        if (index < 0) {
          newArr.push(newMembership);
        } else {
          newArr[index] = newMembership;
        }
        newArr.sort((a, b) => a.groupname.localeCompare(b.groupname));
        this.setState({ groupMemberships: newArr });
      }
    } catch (err) {
      console.error("Error joining group:", err);
      this.setState({ errMsg: (err as Error).message });
    }
  }

  async recoverGroup(groupID: string): Promise<void> {
    const context: AuthContext = this.context;
    if (!context.membership) {
      this.setState({ errMsg: "Missing Account Info!" });
      return;
    }
    try {
      const groupService = groupServiceFactory()

      await groupService.reWelcomeGroup(context.membership.accountID, groupID);

      const groupMembership: GroupMembership | undefined = this.state.groupMembershipMap[groupID];
      if (!groupMembership) { return; }

      groupMembership.outOfSync = false;

      this.updateGroupList(groupMembership);

    } catch (err) {
      console.error("Error recovering group:", err);
      this.setState({ errMsg: (err as Error).message });
    }
  }

  async enableArchiving(groupID: string): Promise<void> {
    const context: AuthContext = this.context;
    if (!context.membership) {
      this.setState({ errMsg: "Missing Account Info!" });
      return;
    }
    await groupServiceFactory().enableArchiving(context.membership.accountID, groupID);

    const groupMembership: GroupMembership | undefined = this.state.groupMembershipMap[groupID];
    if (!groupMembership) { return; }
    groupMembership.needArchiving = false;
    this.updateGroupList(groupMembership);
  }

  private updateGroupList(groupMembership: GroupMembership, resortDMs: boolean = false, forceDMs: boolean = false) {
    if (groupMembership.grouptype === GroupType.dm) {
      if (forceDMs || groupMembership.haveRatchetTree) {
        const newArr = Array.from(this.state.directMessages);
        if (resortDMs) {
          newArr.sort(function (a, b): number {
            const aMillis = (a.lastMsgTime || DateTime.fromMillis(0)).toMillis();
            const bMillis = (b.lastMsgTime || DateTime.fromMillis(0)).toMillis();
            return bMillis - aMillis;
          });
        }
        this.setState({ directMessages: newArr });
      }
    } else if (groupMembership.grouptype !== GroupType.meeting) {
      this.setState({ groupMemberships: Array.from(this.state.groupMemberships) });
    }
  }

  public async getMatchingMembers(queryText: string, groupID: string): Promise<Array<AccountMemberMention>> {
    if (this.searchResolver) {
      this.searchResolver([]);
    }
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    return new bluebird((resolve) => {
      this.searchResolver = resolve;
      this.searchTimer = setTimeout(() => {
        this.searchResolver = null;
        this.searchTimer = null;
        resolve(this.fetchMatchingMembers(queryText, groupID));
      }, 500);
    });
  }

  private async fetchMatchingMembers(queryText: string, groupID: string): Promise<Array<AccountMemberMention>> {

    let prefix = "@";
    queryText = queryText.toLowerCase().trim();
    if (queryText.startsWith("!!")) {
      queryText = queryText.substring(2);
      prefix = "@!!";
    } else if (queryText.startsWith("!")) {
      queryText = queryText.substring(1);
      prefix = "@!";
    } else if (queryText.startsWith("-")) {
      queryText = queryText.substring(1);
      prefix = "@-";
    }
    if (queryText.startsWith("(")) {
      queryText = queryText.substring(1);
    } else {
      const firstSpace = queryText.indexOf(" ");
      if (firstSpace > 0) {
        return bluebird.resolve([]);
      }
    }

    const matches: Array<AccountMemberMention> = [];



    if (/^al?l?/.test(queryText)) {
      matches.push({
        id: `${prefix}all`,
        userID: "team",
        firstname: "",
        lastname: "",
        email: "",
        level: prefix,
        team:"all"
      });
    }
    const adminService = adminAccountServiceFactory();
    const authService = authServiceFactory();

    const [teams, members] = await bluebird.all([
      adminService.getTeams(this.context.membership?.accountID as string, queryText, 10),
      authService.getAccountMembersForGroup(groupID, queryText, 10)

    ])

    for (const team of teams) {
      matches.push({
        id: `${prefix}${team.teamName}`,
        userID: "team",
        firstname: "",
        lastname: "",
        email: "",
        level: prefix,
        team:`${team.teamName}`,
        teamID:`${team._id}`
      })
    }
    //let count = 0;
    //for (const userID in this.state.acctMemberMap) {
    //  const mem = this.state.acctMemberMap[userID];
    //  if (mem.firstname.toLowerCase().includes(queryText) ||
    //      mem.lastname.toLowerCase().includes(queryText) ||
    //      mem.email?.includes(queryText)) {
    for (const mem of members) {
      matches.push({
        id: prefix + mem.firstname + " " + mem.lastname,
        userID: mem.userID,
        firstname: mem.firstname,
        lastname: mem.lastname,
        email: mem.email || "",
        level: prefix
      });
      //count++;
      //}
      //if (count >= 10) { break; }
    }
    this.searchResolver = null;
    this.searchTimer = null;
    return matches;
  }

  public toggleGroupList(): void {
    this.setState({
      classes: {
        ...this.state.classes,
        showLeftNav: !this.state.classes.showLeftNav
      }
    });
  }

  public toggleRightDetail(): void {
    const currentlyShowing = this.state.classes.showRightDetail;
    this.setState({
      currentThreadID: this.state.classes.showRightDetail ?
        "" : this.state.currentThreadID,
      classes: {
        ...this.state.classes,
        showRightDetail: !this.state.classes.showRightDetail
      }
    });
    if (currentlyShowing && this.state.currentGroup) {
      setTimeout(() => {
        this.props.navigate(
          `/group/${(this.state.currentGroup as GroupMembership).groupID}`);
      }, 500);
    }
  }

  public closeEditMeetingDialog(): void {
    this.setState({ openEditMeetingDialog: false });
  }

  public finishEditMeetingDialog(meeting: Meeting): void {
    this.setState({ lastCreatedMeeting: meeting });
  }



  render(): React.ReactElement<any, any> {
    const classNames = Object.keys(this.state.classes)
      .filter(cls => this.state.classes[cls as keyof ClassToggles])
      .join(" ");

    return <GroupNavContext.Provider value={this.state}>
      <div className={classNames}>
        <div className="background-overlay" onClick={this.hideLeftNav} />
        <nav className="left-nav with-footer">
          <LeadingSideInner>
            <div className="choosers-container">
              <AccountChooser />
              <div className="account-container">
                <header className="iris-header">
                  <img className="logo-img" src="/img/mercury-workspace.png" />
                </header>
                <AccountHeader />
                <GroupListContainer
                  currentGroup={this.state.currentGroup}
                  groups={this.state.groupMemberships}
                  directMessages={this.state.directMessages}
                  unreadThreadMessages={this.state.unreadThreadMessages}
                  unreadThreadMentions={this.state.unreadThreadMentions}
                  selectedGroup={this.changeGroup}
                  onDoAddGroup={() => this.setState({ showAddGroupModal: true })}
                  onOpenEditMeetingDialog={() => this.setState({ openEditMeetingDialog: true })} />
              </div>
            </div>
          </LeadingSideInner>
          <AccountDisplayFooter />
        </nav>
        <div className="both-side-margin main-section-container" role="main">

          {this.state.huddleGroup && <HuddleContainer groupMembership={this.state.huddleGroup} />}

          <div ref={this.mainScrollRef} className="main-scroll-container">
            <Routes>
              <Route path="profile" element={<AccountProfileContainer />} />
              <Route path="browse" element={
                <GroupBrowserComponent
                  accountID={this.context.membership?.accountID || ""}
                  memberships={this.state.groupMemberships}
                  onJoinGroup={group => this.addThisDeviceToGroup(group._id)}
                />}
              />
              <Route path="threads" element={<ThreadListViewComponent groupNavContext={this.state} />} />
              <Route path="newDM" element={<NewDirectMessageComponent />} />
              <Route path=":groupID/members" element={<GroupMemberListComponent />} />
              <Route path=":groupID/files" element={<GroupFileListComponent />} />
              <Route path=":groupID/settings" element={<GroupSettingsComponent />} />
              <Route path=":groupID?/thread?/:threadID?" element={<GroupChatViewComponent />} />
            </Routes>
          </div>
        </div>
        <aside className="right-detail trailing-side-container">
          <Routes>
            <Route path=":groupID/thread/:threadID" element={<ThreadChatViewWrapper />} />
          </Routes>
        </aside>
        <AddGroupDialog
          open={this.state.showAddGroupModal}
          membership={(this.context as AuthContext).membership}
          onFinishGroup={this.finishAddGroup}
          onClose={() => this.setState({ showAddGroupModal: false })} />

        {this.state.openEditMeetingDialog && <EditMeetingDialog
          open={this.state.openEditMeetingDialog}
          meeting={this.state.lastCreatedMeeting}
          onClose={this.closeEditMeetingDialog}
          didChangeMeeting={this.finishEditMeetingDialog} />}
        <ErrorSnackBar errMsg={this.state.errMsg} didClose={() => this.setState({ errMsg: "" })} />
      </div>
    </GroupNavContext.Provider>;
  }
}
